import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Referral codes are non-sensitive marketing tags, but we still stash them
// in sessionStorage (not localStorage) so they clear when the tab closes —
// keeps the browser storage surface small and satisfies the "no PII in
// localStorage" static-analysis rule. A ref code arriving in ?ref= survives
// the register flow because both events happen in the same tab session.
const REF_STORAGE_KEY = "bookforge_ref_code";
const _store = (typeof window !== "undefined") ? window.sessionStorage : null;

const _readStoredRef = () => {
  try { return (_store?.getItem(REF_STORAGE_KEY) || "").toUpperCase(); }
  catch (e) {
    console.debug("sessionStorage read blocked:", e.message);
    return "";
  }
};

const _writeStoredRef = (v) => {
  try { _store?.setItem(REF_STORAGE_KEY, v); }
  catch (e) { console.debug("sessionStorage write blocked (private mode?):", e.message); }
};

const _clearStoredRef = () => {
  try { _store?.removeItem(REF_STORAGE_KEY); }
  catch (e) { console.debug("sessionStorage removeItem blocked:", e.message); }
};

/** Hook that owns the Register form state + submission. Returns the same
 * shape whether the referral code came from ?ref= or localStorage. */
export function useRegisterForm(onSuccess) {
  const { register, formatApiError } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  // iter-41: allow admin-shared signup links to prefill the email
  // (`/register?email=alice@example.com`). Keeps password blank so the
  // invitee still sets their own.
  const [email, setEmail] = useState(() => (params.get("email") || "").trim().toLowerCase());
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [refCode, setRefCode] = useState(() => {
    const q = (params.get("ref") || "").trim().toUpperCase();
    if (q) { _writeStoredRef(q); return q; }
    return _readStoredRef();
  });

  useEffect(() => {
    const q = (params.get("ref") || "").trim().toUpperCase();
    if (q && q !== refCode) { setRefCode(q); _writeStoredRef(q); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await register(name, email, password, refCode || null);
      _clearStoredRef();
      toast.success(refCode ? "Account created — referral applied" : "Account created");
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return { name, setName, email, setEmail, password, setPassword,
           refCode, busy, submit };
}
